/**
 * Pure helpers for event form state shape, client-side image handling, and API payload construction.
 * Kept out of React components so `Event.js` stays orchestration/UI wiring and these pieces stay testable.
 */
import { uploadCroppedImageToS3 } from "../../../api/imageUpload";

const DEFAULT_INVITE_COPY = "YOUR'RE INVITED";
const DEFAULT_PRIMARY_BUTTON = 'Tell me more';
const DEFAULT_SECONDARY_BUTTON = "I'm not interested";

const EVENT_TYPE_OPTIONS = {
    aisle: [
        { value: 'aisle_events', label: 'aisle_events', maleBaseAmount: 1500, femaleBaseAmount: 1000 },
        { value: 'aisle_events_spl', label: 'aisle_events_spl', maleBaseAmount: 1000, femaleBaseAmount: 500 },
        { value: 'aisle_events_partnered', label: 'aisle_events_partnered', maleBaseAmount: 3000, femaleBaseAmount: 2500 }
    ],
    arike: [
        { value: 'arike_event', label: 'arike_event', maleBaseAmount: 300, femaleBaseAmount: 250 }
    ]
};

const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toAmountString = (value) => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed) : '';
};

const getEventTypeOption = (appName, eventType) => {
    const normalizedAppName = appName?.toLowerCase?.() || '';
    return (EVENT_TYPE_OPTIONS[normalizedAppName] || []).find((option) => option.value === eventType) || null;
};

export const deriveOfflineEventPricingState = ({
    appName,
    eventType,
    maleDiscount,
    femaleDiscount,
    maleAmount,
    femaleAmount
}) => {
    const eventTypeOption = getEventTypeOption(appName, eventType);
    const maleBaseAmount = toAmountString(eventTypeOption?.maleBaseAmount);
    const femaleBaseAmount = toAmountString(eventTypeOption?.femaleBaseAmount);

    const shouldInferMaleDiscount = maleBaseAmount !== ''
        && (maleDiscount === '' || maleDiscount === null || maleDiscount === undefined)
        && (maleAmount !== '' && maleAmount !== null && maleAmount !== undefined);
    const shouldInferFemaleDiscount = femaleBaseAmount !== ''
        && (femaleDiscount === '' || femaleDiscount === null || femaleDiscount === undefined)
        && (femaleAmount !== '' && femaleAmount !== null && femaleAmount !== undefined);

    const resolvedMaleDiscount = shouldInferMaleDiscount
        ? toAmountString(Math.max((toNumber(maleBaseAmount) ?? 0) - (toNumber(maleAmount) ?? 0), 0))
        : toAmountString(maleDiscount);

    const resolvedFemaleDiscount = shouldInferFemaleDiscount
        ? toAmountString(Math.max((toNumber(femaleBaseAmount) ?? 0) - (toNumber(femaleAmount) ?? 0), 0))
        : toAmountString(femaleDiscount);

    const resolvedMaleAmount = maleBaseAmount === ''
        ? toAmountString(maleAmount)
        : toAmountString(Math.max((toNumber(maleBaseAmount) ?? 0) - (toNumber(resolvedMaleDiscount) ?? 0), 0));

    const resolvedFemaleAmount = femaleBaseAmount === ''
        ? toAmountString(femaleAmount)
        : toAmountString(Math.max((toNumber(femaleBaseAmount) ?? 0) - (toNumber(resolvedFemaleDiscount) ?? 0), 0));

    return {
        maleBaseAmount,
        femaleBaseAmount,
        maleDiscount: resolvedMaleDiscount,
        femaleDiscount: resolvedFemaleDiscount,
        maleAmount: resolvedMaleAmount,
        femaleAmount: resolvedFemaleAmount
    };
};

/**
 * Returns a blank form model with safe defaults (ages, copy, empty lists, no pending images).
 * Factory function so every reset/new/edit hydration gets a fresh object and React state updates reliably.
 */
export const createInitialEventFormState = () => ({
    eventId: null,
    eventName: '',
    eventType: '',
    offlineEventCityId: '',
    maleSlots: '',
    femaleSlots: '',
    minMaleAge: 18,
    maxMaleAge: 18,
    minFemaleAge: 18,
    maxFemaleAge: 18,
    eventDate: '',
    eventLocation: '',
    maleBaseAmount: '',
    femaleBaseAmount: '',
    maleDiscount: '',
    femaleDiscount: '',
    maleAmount: '',
    femaleAmount: '',
    iosVersion: '',
    androidVersion: '',
    timeFrom: '',
    timeTo: '',
    templateSlug: '',
    webTitle: '',
    webDescription: '',
    note: '',
    appTitle: '',
    appDescription: '',
    youAreInvited: DEFAULT_INVITE_COPY,
    buttonText1: DEFAULT_PRIMARY_BUTTON,
    buttonText2: DEFAULT_SECONDARY_BUTTON,
    emailSubject: '',
    whatsIncludedPoints: [],
    newWhatsIncludedPoint: '',
    termsAndConditionPoints: [],
    newTermsAndConditionPoint: '',
    importantInformationPoints: [],
    newImportantInformationPoint: '',
    appImageUrl: '',
    appImagePreview: '',
    pendingAppImageUpload: '',
    hasAppImageChanged: false,
    webImageUrl: '',
    webImagePreview: '',
    pendingWebImageUpload: '',
    hasWebImageChanged: false
});

/**
 * Maps an API event record into the same camelCase shape as `createInitialEventFormState`.
 * Centralizes snake_case → form field mapping so the edit screen and list row stay in sync with one definition.
 */
export const createEventFormStateFromEvent = (event) => ({
    eventId: event.event_id,
    eventName: event.event_name || '',
    eventType: event.event_type || '',
    offlineEventCityId: event.offline_event_city_id ? String(event.offline_event_city_id) : '',
    maleSlots: event.male_slots || '',
    femaleSlots: event.female_slots || '',
    minMaleAge: event.min_male_age || 18,
    maxMaleAge: event.max_male_age || 18,
    minFemaleAge: event.min_female || event.min_female_age || 18,
    maxFemaleAge: event.max_female_age || 18,
    eventDate: event.event_date || '',
    eventLocation: event.event_location || '',
    iosVersion: event.minimum_ios_version_required || '',
    androidVersion: event.minimum_android_version_required || '',
    timeFrom: event.event_start_time || '',
    timeTo: event.event_end_time || '',
    templateSlug: event.template_slug || '',
    webTitle: event.web_title || '',
    webDescription: event.web_description || '',
    note: event.event_data?.note || '',
    appTitle: event.event_app_data?.app_title || '',
    appDescription: event.event_app_data?.app_description || '',
    youAreInvited: event.event_app_data?.you_are_invited || DEFAULT_INVITE_COPY,
    buttonText1: event.event_app_data?.button_text_1 || DEFAULT_PRIMARY_BUTTON,
    buttonText2: event.event_app_data?.button_text_2 || DEFAULT_SECONDARY_BUTTON,
    emailSubject: event.email_subject || '',
    whatsIncludedPoints: event.event_data?.whats_included || [],
    newWhatsIncludedPoint: '',
    termsAndConditionPoints: event.event_data?.terms_and_conditions || [],
    newTermsAndConditionPoint: '',
    importantInformationPoints: event.event_data?.important_information || [],
    newImportantInformationPoint: '',
    appImageUrl: event.app_pop_up_image || '',
    appImagePreview: event.app_pop_up_image || '',
    pendingAppImageUpload: '',
    hasAppImageChanged: false,
    webImageUrl: event.website_pop_up_image || '',
    webImagePreview: event.website_pop_up_image || '',
    pendingWebImageUpload: '',
    hasWebImageChanged: false,
    ...deriveOfflineEventPricingState({
        appName: event.app_name,
        eventType: event.event_type || '',
        maleDiscount: '',
        femaleDiscount: '',
        maleAmount: event.male_amount || '',
        femaleAmount: event.female_amount || ''
    })
});

/**
 * Wraps `FileReader.readAsDataURL` in a Promise for async/await in the parent component.
 * Isolated here so image pickers do not duplicate reader error handling.
 */
export const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (loadEvent) => resolve(loadEvent.target?.result || '');
    reader.onerror = () => reject(new Error('file_read_error'));

    reader.readAsDataURL(file);
});

/**
 * Ensures both app and web images exist either as existing URLs or pending uploads before save.
 * Alerts with copy that differs for create vs edit; returns a boolean so callers can short-circuit saves.
 */
export const validateEventFormImages = (formValues, isEdit) => {
    if (!formValues.appImageUrl && !formValues.pendingAppImageUpload) {
        alert(isEdit ? 'Please upload the app pop up image' : 'Please upload both app and web images');
        return false;
    }

    if (!formValues.webImageUrl && !formValues.pendingWebImageUpload) {
        alert(isEdit ? 'Please upload the web pop up image' : 'Please upload both app and web images');
        return false;
    }

    return true;
};

/**
 * Ensures the required offline city selector has a value before save.
 * Keeps validation centralized with the other non-HTML checks used by the save pipeline.
 */
export const validateEventFormCity = (formValues) => {
    if (!formValues.offlineEventCityId) {
        alert('Please select a city');
        return false;
    }

    return true;
};

export const getEventTypeOptions = (appName) => {
    const normalizedAppName = appName?.toLowerCase?.() || '';
    return (EVENT_TYPE_OPTIONS[normalizedAppName] || []).map(({ maleBaseAmount, femaleBaseAmount, ...option }) => option);
};

export const getEventTypePricingOption = (appName, eventType) => getEventTypeOption(appName, eventType);

export const getEventTypePricingSummary = (appName, eventType) => {
    const pricingOption = getEventTypeOption(appName, eventType);

    if (!pricingOption) {
        return null;
    }

    return {
        maleBaseAmount: toAmountString(pricingOption.maleBaseAmount),
        femaleBaseAmount: toAmountString(pricingOption.femaleBaseAmount)
    };
};

/**
 * Returns the existing image URL when unchanged, otherwise uploads the pending data URL to S3.
 * Shared by app and web flows inside `resolveEventImageUrls` to avoid duplicating upload branching.
 */
const resolveSingleImageUrl = async ({ existingUrl, pendingUpload, hasChanged, uploadReference }) => {
    if (!hasChanged) {
        return existingUrl;
    }

    const uploadedUrl = await uploadCroppedImageToS3(pendingUpload, uploadReference);
    return uploadedUrl || null;
};

/**
 * Resolves final app and web image URLs (re-upload when flagged changed, otherwise keep server URLs).
 * Returns `{ appImageUrl, webImageUrl }` or `null` if either side fails so the save path can abort cleanly.
 */
export const resolveEventImageUrls = async (formValues, appName) => {
    // const uploadReference = formValues.eventId || formValues.eventName || `${appName}-event`;
    const appImageUrl = await resolveSingleImageUrl({
        existingUrl: formValues.appImageUrl,
        pendingUpload: formValues.pendingAppImageUpload,
        hasChanged: formValues.hasAppImageChanged,
        uploadReference: `53BB3571`
    });

    if (!appImageUrl) {
        return null;
    }

    const webImageUrl = await resolveSingleImageUrl({
        existingUrl: formValues.webImageUrl,
        pendingUpload: formValues.pendingWebImageUpload,
        hasChanged: formValues.hasWebImageChanged,
        uploadReference: `53BB3571`
    });

    if (!webImageUrl) {
        return null;
    }

    return { appImageUrl, webImageUrl };
};

/**
 * Builds `multipart/form-data` for `events/create_event`, including nested JSON blobs and optional `event_id` for edit.
 * Lives in utils so field names and JSON structure stay aligned with the backend contract in one module.
 */
export const buildEventFormData = ({ formValues, appName, imageUrls, isEdit }) => {
    const formData = new FormData();

    formData.append('name', formValues.eventName);
    formData.append('event_type', formValues.eventType);
    formData.append('male_slots', formValues.maleSlots);
    formData.append('female_slots', formValues.femaleSlots);
    formData.append('min_male_age', formValues.minMaleAge);
    formData.append('min_female', formValues.minFemaleAge);
    formData.append('max_male_age', formValues.maxMaleAge);
    formData.append('max_female_age', formValues.maxFemaleAge);
    formData.append('minimum_android_version_required', formValues.androidVersion);
    formData.append('minimum_ios_version_required', formValues.iosVersion);
    formData.append('template_slug', formValues.templateSlug);
    formData.append('event_date', formValues.eventDate);
    formData.append('event_start_time', formValues.timeFrom);
    formData.append('event_end_time', formValues.timeTo);
    formData.append('event_location', formValues.eventLocation);
    formData.append('offline_event_city_id', formValues.offlineEventCityId);
    formData.append('app_name', appName);
    formData.append('male_discount', formValues.maleDiscount);
    formData.append('female_discount', formValues.femaleDiscount);
    formData.append('male_amount', formValues.maleAmount);
    formData.append('female_amount', formValues.femaleAmount);
    formData.append('app_pop_up_image', imageUrls.appImageUrl);
    formData.append('website_pop_up_image', imageUrls.webImageUrl);
    formData.append('email_subject', formValues.emailSubject);
    formData.append('event_data', JSON.stringify({
        whats_included: formValues.whatsIncludedPoints,
        important_information: formValues.importantInformationPoints,
        terms_and_conditions: formValues.termsAndConditionPoints,
        note: formValues.note
    }));
    formData.append('event_app_data', JSON.stringify({
        app_title: formValues.appTitle,
        app_description: formValues.appDescription,
        you_are_invited: formValues.youAreInvited,
        button_text_1: formValues.buttonText1,
        button_text_2: formValues.buttonText2
    }));
    formData.append('web_title', formValues.webTitle);
    formData.append('web_description', formValues.webDescription);

    if (isEdit && formValues.eventId) {
        formData.append('event_id', formValues.eventId);
    }

    return formData;
};
